import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import {
  AccessLevelEntityType,
  getAccessLevelItems,
} from "@components/access-level/access-level-utils";
import { blue, Checkmark2Icon, neutral } from "@flat-ui";
import { Box } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { colorConst, EDecimalToHex } from "@styles/common-colors";
import { DEFAULT_INPUT_FONT_SIZE } from "@styles/common-styles";
import { addTransparency } from "@utils/ui-utils";

interface Props {
  /** Type of the entity to which the access level belongs, it is used in the access level descriptions */
  type: AccessLevelEntityType;

  /** Current access level */
  currentAccessLevel?: FaroCoreApiTypes.ProjectAccessLevel;

  /** Callback when user selects an access level from the dropdown */
  onSelectAccessLevel: (
    accessLevel: FaroCoreApiTypes.ProjectAccessLevel
  ) => void;
}

/** Access level dropdown */
export function AccessLevelDropdown({
  type,
  currentAccessLevel,
  onSelectAccessLevel,
}: Props): JSX.Element {
  return (
    <>
      {getAccessLevelItems(type).map((accessLevel) => (
        <FaroMenuItem
          data-my-value={accessLevel.value}
          key={accessLevel.value}
          value={accessLevel.value}
          onClick={() => onSelectAccessLevel(accessLevel.value)}
          sx={{
            display: "block",
            padding: "12px",
            mb: "2px",
            whiteSpace: "normal",
            "&:hover": {
              backgroundColor: addTransparency({
                color: neutral[500],
                alpha: EDecimalToHex.twentySix,
              }),

              // Set menu item label color to blue500 on hover
              "& .accessLevelItem-label": {
                color: blue[500],
              },
            },
            "&.MuiButtonBase-root": {
              backgroundColor:
                accessLevel.value === currentAccessLevel
                  ? addTransparency({
                      color: neutral[500],
                      alpha: EDecimalToHex.thirtyEight,
                    })
                  : "transparent",

              "&:hover": {
                backgroundColor: addTransparency({
                  color: neutral[500],
                  alpha: EDecimalToHex.thirtyEight,
                }),
              },

              // Overrides default style for ".Mui-focusVisible" class
              "&.Mui-focusVisible": {
                backgroundColor: addTransparency({
                  color: neutral[500],
                  alpha: EDecimalToHex.thirtyEight,
                }),
              },
            },
          }}
        >
          {/* Title and icon */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: "9px",
            }}
          >
            <Box
              className="accessLevelItem-label"
              sx={{
                color:
                  accessLevel.value === currentAccessLevel
                    ? blue[500]
                    : colorConst.normalFont,
                fontSize: DEFAULT_INPUT_FONT_SIZE,
                fontWeight: "600",
              }}
            >
              {accessLevel.label}
            </Box>

            {accessLevel.value === currentAccessLevel && (
              <Checkmark2Icon
                sx={{
                  width: "21px",
                  height: "21px",
                  fill: blue[500],
                }}
              />
            )}
          </Box>

          {/* Description */}
          <Box
            sx={{
              color: neutral[600],
              fontSize: "12px",
            }}
          >
            {accessLevel.description}
          </Box>
        </FaroMenuItem>
      ))}
    </>
  );
}
